import React from 'react';
import PropTypes from 'prop-types';

import PaxTypeSelector from '../../common/PaxTypeSelector';
import ArrowIcon from '../../icons/ArrowIcon';
import CloseIcon from '../../icons/CloseIcon';

import { isValidAge, getUseKey } from '../../../utils/Room';

const getUseStateValues = (room) => {
  // IMPORTANT: The data in the state replicates the data that is submitted to the API, so it needs this structure
  // { adults, childs, infants, ages }
  const roomUse = room.use_requested;
  return {
    ...roomUse,
    ages: roomUse.ages || [],
    totalPax: roomUse.adults + roomUse.childs, // Only the sum of ADL + CHD
  };
};

class FreeModePaxSelector extends React.Component {
  constructor(props) {
    super(props);

    const useState = getUseStateValues(this.props.room);
    this.state = { ...useState, showDialog: false, invalidSelection: false };

    this.onToggleUpdateDialog = this.onToggleUpdateDialog.bind(this);
    this.onClickUpdatePax = this.onClickUpdatePax.bind(this);
    this.onClickAge = this.onClickAge.bind(this);
    this.onUpdateOccupancy = this.onUpdateOccupancy.bind(this);
  }

  // eslint-disable-next-line react/no-deprecated
  componentWillReceiveProps(nextProps) {
    // Whenever there is a change in "use_requested" for this room, the paxes need to match the new distribution
    const prevKey = getUseKey(this.props.room.use_requested);
    const nextKey = getUseKey(nextProps.room.use_requested);
    if (prevKey !== nextKey) {
      this.setState(getUseStateValues(nextProps.room));
    }
  }

  onToggleUpdateDialog(ev) {
    let stateUpdate = {};
    if (!ev.skipReset) {
      // If they don't click on update, then the distribution is reset to the last requested one
      stateUpdate = getUseStateValues(this.props.room);
    }
    this.setState({ showDialog: !this.state.showDialog, ...stateUpdate });
  }

  onUpdateOccupancy() {
    // The guest menu is closed and then we pass the updated guests to the parent
    this.onToggleUpdateDialog({ skipReset: true });
    this.props.onOccupancyUpdate(this.state);
  }

  getUpdatedChildrenAges(paxKey, diff) {
    const forInfants = paxKey === 'infants';
    const { hotel } = this.props;
    // Add a new child age of the same type
    if (diff === 1) {
      return this.state.ages.concat(forInfants ? 0 : hotel.max_child_age);
    }
    // This is the case for removing one child, since the ages are mixed in one array, we need to do some checks
    const agesToRemoveFrom = this.state.ages.filter((age) => isValidAge(hotel, paxKey, age));
    const agesToKeep = [];
    const newAges = [];

    this.state.ages.forEach(((age) => {
      if (!isValidAge(hotel, paxKey, age)) {
        // we keep the ages of the children of the other type
        newAges.push(age);
      } else if (agesToKeep.length < agesToRemoveFrom.length - 1) {
        // and for the type that has one child removed, we make sure to add all of them except the last
        agesToKeep.push(age);
        newAges.push(age);
      }
    }));
    return newAges;
  }

  onClickUpdatePax(paxKey, diff) {
    // Update paxes of the selected type, and the total occupants (babies do not count for it)
    const newPaxNumber = this.state[paxKey] + diff;
    const isInfants = paxKey === 'infants';
    const newTotalPax = isInfants ? this.state.totalPax : this.state.totalPax + diff;

    const stateUpdate = {
      [paxKey]: newPaxNumber,
      totalPax: newTotalPax,
    };

    // When the children number changes, we need to update their ages
    if (paxKey !== 'adults') {
      stateUpdate.ages = this.getUpdatedChildrenAges(paxKey, diff);
    }
    // We allow selecting an occupancy below the minimum, but a message will be shown
    stateUpdate.invalidSelection = newTotalPax < this.props.room.min_pax;
    this.setState(stateUpdate);
  }

  onClickAge(paxKey, ageIndex, diff) {
    // If they go above the limits, we won't update the age (would be better to disable the buttons)
    const newAge = this.state.ages[ageIndex] + diff;
    if (!isValidAge(this.props.hotel, paxKey, newAge)) {
      return;
    }
    const updatedAges = this.state.ages.map((age, index) => {
      if (index === ageIndex) {
        return newAge;
      }
      return age;
    });
    this.setState({ ages: updatedAges });
  }

  hasUnchangedOccupancy() {
    // Returns true if the occupancy in the Component is the same as in the original properties
    const {
      adults, childs, infants, ages = [],
    } = this.props.room.use_requested;
    const numberSame = this.state.adults === adults && this.state.childs === childs && this.state.infants === infants;
    const agesSame = ages.join(',') === this.state.ages.join(',');
    return numberSame && agesSame;
  }

  renderInvalidOccupancy() {
    // The only invalid occupancy that can be set is for total pax < min allowed
    return (
      <p className="alert-danger m-1 p-2 border-primary border font-weight-bold">
        {this.context.t('Occupancy below min paxes')}
      </p>
    );
  }

  renderAdultControls(hotel) {
    return (
      <PaxTypeSelector paxKey="adults" paxNum={this.state.adults} totalPax={this.state.totalPax}
                       hotel={hotel} room={this.props.room} onClick={this.onClickUpdatePax} />
    );
  }

  renderChildrenControls(hotel) {
    if (this.props.room.max_childs === 0) {
      return null;
    }
    return (
      <PaxTypeSelector paxKey="childs" paxNum={this.state.childs} totalPax={this.state.totalPax}
                       hotel={hotel} room={this.props.room} onClick={this.onClickUpdatePax}
                       onAgeClick={this.onClickAge} ages={this.state.ages} />
    );
  }

  renderInfantsControls(hotel) {
    if (this.props.room.max_infants === 0) {
      return null;
    }
    return (
      <PaxTypeSelector paxKey="infants" paxNum={this.state.infants} totalPax={this.state.totalPax}
                       hotel={hotel} room={this.props.room} onClick={this.onClickUpdatePax}
                       onAgeClick={this.onClickAge} ages={this.state.ages} />
    );
  }

  renderUpdateButton() {
    const disabledClass = this.hasUnchangedOccupancy() ? 'sandbox__btn--disabled' : '';
    return (
      <button className={`sandbox__btn no-shadow sandbox__btn--select-pax ${disabledClass}`}
            onClick={this.onUpdateOccupancy}>
        {this.context.t('Update')}
      </button>
    );
  }

  renderPaxUpdateDialog() {
    const { hotel } = this.props;
    return (
      <div className="pax-selector__dropdown sandbox border-primary">
        {this.renderAdultControls(hotel)}
        {this.renderChildrenControls(hotel)}
        {hotel.allow_infants_enabled && this.renderInfantsControls(hotel)}
        <div className="pax-selector__dropdown__close cursor-pointer" onClick={this.onToggleUpdateDialog}>
          <CloseIcon />
        </div>
        {this.renderUpdateButton()}
      </div>
    );
  }

  render() {
    const { adults, childs, infants } = this.props.room.use_requested;
    return (
      <div className="d-flex mt-2 pax-selector">
        <div className="my-1 mr-2 p-2 px-2 border cursor-pointer" onClick={this.onToggleUpdateDialog}>
          <span className="mr-1">{adults} {this.context.t('adults')}</span>
          {this.props.room.max_childs > 0
            && <span className="mr-1">{childs} {this.context.t('children')}</span>
          }
          {this.props.hotel.allow_infants_enabled && this.props.room.max_infants > 0
            && <span className="mr-1">{infants} {this.context.t('infants')}</span>
          }
          <ArrowIcon direction={this.state.showDialog ? 'up' : 'down'} />
        </div>
        {this.state.invalidSelection && this.renderInvalidOccupancy()}
        {this.state.showDialog && this.renderPaxUpdateDialog()}
      </div>
    );
  }
}

FreeModePaxSelector.propTypes = {
  hotel: PropTypes.object.isRequired,
  room: PropTypes.object.isRequired,
  lang: PropTypes.string.isRequired,
  onOccupancyUpdate: PropTypes.func.isRequired,
};

FreeModePaxSelector.contextTypes = {
  t: PropTypes.func.isRequired,
  router: PropTypes.object.isRequired,
};

export default FreeModePaxSelector;
